// @ts-strict-ignore
import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Text } from '../../components/text';
import { black, charcoal05, darkgray30, primary } from '../../utils/colors';
import Logo from '../../assets/icons/logo.svg';
import { ModuleDefinition } from '../../../types/routes/module';
import { moduleImageURL, assetLinkFromAlignedResourcesGoogleDocs } from '../../utils/module';
import { useAppSelector } from '../../redux';

export const SubjectToolkits: React.FC<{
  subjectModules: string[];
}> = ({ subjectModules }) => {
  const modules = useAppSelector((state) => state.module.definitions.modules);

  // Modules without a toolkit have already been filtered out
  const leafModules: (ModuleDefinition | undefined)[] = subjectModules.map((moduleId) =>
    modules.find((m) => m.id == moduleId),
  );

  leafModules.sort((a: ModuleDefinition | undefined, b: ModuleDefinition | undefined) => {
    const aa = a ? a.name.toUpperCase() : '';
    const bb = b ? b.name.toUpperCase() : '';
    if (aa < bb) return -1;
    if (aa > bb) return 1;
    return 0;
  });

  if (leafModules.length > 0)
    return (
      <Modules>
        {leafModules.map(
          (module: ModuleDefinition | undefined) =>
            module && (
              <Module key={module.id}>
                {module.image ? (
                  <Image src={moduleImageURL + '/' + module.id + '/' + module.image} />
                ) : (
                  <ImageContainer>
                    <Logo />
                  </ImageContainer>
                )}
                <Text variant="nav" center color={black}>
                  {module.name}
                </Text>
                <Text center variant="p">
                  <AssetLink
                    to="/toolkits"
                    onClick={(event) => {
                      event.preventDefault();
                      window.open(module.alignedResourcesLink);
                    }}
                  >
                    Google Doc Version
                  </AssetLink>
                </Text>
                {assetLinkFromAlignedResourcesGoogleDocs(module.alignedResourcesLink) && (
                  <Text center variant="p">
                    <AssetLink
                      to="/toolkits"
                      onClick={(event) => {
                        event.preventDefault();
                        const pdfUrl = assetLinkFromAlignedResourcesGoogleDocs(module.alignedResourcesLink);
                        if (pdfUrl) {
                          window.open(pdfUrl);
                        }
                      }}
                    >
                      PDF version
                    </AssetLink>
                  </Text>
                )}
              </Module>
            ),
        )}
      </Modules>
    );
};

const Modules = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  padding: '1rem 1rem',
});

const Module = styled.div({
  position: 'relative',
  margin: '1rem',
  // The bottom padding gives room for the button bar.
  border: `1px solid ${primary}`,
  borderRadius: `10px`,
  boxShadow: `0 5px 5px ${darkgray30}`,
  padding: '1.25rem 1.25rem 1.25rem',
  width: '10rem',
  display: 'block',
});

const AssetLink = styled(Link)({
  textDecoration: 'none',
  // The bottom padding gives room for the button bar.
  padding: '0.5rem', // 1.25rem',
  borderRadius: `10px`,
  width: '8rem',
  display: 'block',
  cursor: 'pointer',
  '&:hover': {
    border: `0`,
    boxShadow: `0 1px 1px ${darkgray30}`,
  },
});

const Image = styled.div<{ src: string }>(({ src }) => ({
  backgroundImage: `url('${src}')`,
  backgroundSize: 'cover',
  backgroundColor: charcoal05,
  height: '6rem',
  marginBottom: '1rem',
}));

const ImageContainer = styled.div({
  backgroundColor: charcoal05,
  height: '12rem',
  marginBottom: '1rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
